import React, { useState } from "react";
import { Box, Card, CardContent, Typography, Button, Alert, Stack, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import GppBadIcon from "@mui/icons-material/GppBad";
import BugReportIcon from "@mui/icons-material/BugReport";
import { deleteCookie, getCookie } from "../../utils/api";
import { api } from "../../utils/api";

export const DevSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState<string | null>(null);
  const [meStatus, setMeStatus] = useState<string | null>(null);
  const [refreshStatus, setRefreshStatus] = useState<string | null>(null);

  const hasAccess = !!getCookie("accessToken");
  const hasRefresh = !!getCookie("refreshToken");

  const invalidateAccessToken = () => {
    deleteCookie("accessToken");
    setMessage("Access token invalidated. Next API call should trigger refresh.");
    window.setTimeout(() => setMessage(null), 3000);
  };

  const invalidateRefreshToken = () => {
    deleteCookie("refreshToken");
    setMessage("Refresh token invalidated. Refresh flow should fail on next 401.");
    window.setTimeout(() => setMessage(null), 3000);
  };

  const testMeEndpoint = async () => {
    setMeStatus("Loading...");
    try {
      const resp = await api(`/admin/v1/me`);
      const text = await resp.text().catch(() => "");
      setMeStatus(`${resp.status} ${resp.statusText}${text ? `: ${text.slice(0, 120)}${text.length > 120 ? '…' : ''}` : ''}`);
    } catch (e: any) {
      setMeStatus(`Error: ${e?.message || 'unknown error'}`);
    }
  };

  const callProtectedRefresh = async () => {
    setRefreshStatus("Calling refresh endpoint...");
    try {
      const refresh = getCookie("refreshToken");
      if (!refresh) {
        setRefreshStatus("Missing tokens: refresh token not present");
        return;
      }
      const resp = await api(`/admin/v1/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      let body = '';
      try {
        body = await resp.text();
      } catch {
        body = '';
      }
      // If response carries new tokens, update cookies view on next render
      try {
        const data = JSON.parse(body || '{}');
        if (data?.access_token || data?.refresh_token) {
          // Let the normal flow handle cookie setting elsewhere; here we just show the payload
        }
      } catch {
        // non-JSON response
      }
      setRefreshStatus(`${resp.status} ${resp.statusText}${body ? `: ${body.slice(0, 200)}${body.length > 200 ? '…' : ''}` : ''}`);
    } catch (e: any) {
      setRefreshStatus(`Error: ${e?.message || 'unknown error'}`);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Typography variant="h4" gutterBottom>
        Dev Settings
      </Typography>

      <Card sx={{ mb: 2, bgcolor: "primary.light" }}>
        <CardContent>
          <Stack direction="column" spacing={2}>
            <Typography variant="h6">Sentry Monitoring</Typography>
            <Typography variant="body2" color="text.secondary">
              Test and debug the Sentry integration, view quota usage, and trigger test errors.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<BugReportIcon />}
              onClick={() => navigate("/dev-settings/sentry-debug")}
              sx={{ alignSelf: "flex-start" }}
            >
              Sentry Debug Console
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {message && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="column" spacing={2}>
            <Typography variant="h6">Tokens</Typography>
            <Typography variant="body2" color="text.secondary">
              Use these for local testing of the 401 → refresh flow.
            </Typography>

            <Stack direction="row" spacing={1}>
              <Chip label={`accessToken: ${hasAccess ? 'present' : 'missing'}`} color={hasAccess ? 'success' : 'default'} variant={hasAccess ? 'filled' : 'outlined'} />
              <Chip label={`refreshToken: ${hasRefresh ? 'present' : 'missing'}`} color={hasRefresh ? 'success' : 'default'} variant={hasRefresh ? 'filled' : 'outlined'} />
            </Stack>

            <Button
              variant="outlined"
              color="warning"
              startIcon={<GppBadIcon />}
              onClick={invalidateAccessToken}
              sx={{ alignSelf: "flex-start" }}
            >
              Invalidate Access Token
            </Button>

            <Button
              variant="outlined"
              color="error"
              startIcon={<GppBadIcon />}
              onClick={invalidateRefreshToken}
              sx={{ alignSelf: "flex-start" }}
            >
              Invalidate Refresh Token
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction="column" spacing={2}>
            <Typography variant="h6">Test</Typography>
            <Typography variant="body2" color="text.secondary">
              Calls <code>/admin/v1/me</code> through the app API wrapper.
            </Typography>
            <Button variant="contained" onClick={testMeEndpoint} sx={{ alignSelf: 'flex-start' }}>
              Test /me
            </Button>
            {meStatus && (
              <Alert severity="info">{meStatus}</Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              Manual refresh call (protected): posts to <code>/refresh/</code> with Authorization and refresh_token from cookies.
            </Typography>
            <Button variant="outlined" onClick={callProtectedRefresh} sx={{ alignSelf: 'flex-start' }}>
              Call Protected Refresh
            </Button>
            {refreshStatus && (
              <Alert severity="info">{refreshStatus}</Alert>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DevSettingsPage;
